"use client";

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { injectMetaTags } from "@/lib/seoHelpers";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import { costSections, costFaq, costServiceLinks, costMaintenanceItems } from "@/mocks/blogCost";

export default function WindowRepairCostMiami() {
  const { t, currentLang } = useLanguage();
  useHreflang();
  const isEs = currentLang === "es";

  useEffect(() => {
    const title = isEs
      ? "Guía de Costos de Reparación de Ventanas Miami — Precios Honestos 2026"
      : "Miami Window Repair Cost Guide — Honest Pricing 2026";
    const desc = t("blog.cost.metaDesc") as string;

    document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);

    const baseUrl = "https://impact-window-repair-miami.com";
    const path = isEs ? "/es/blog/costo-reparacion-ventanas-miami/" : "/blog/window-repair-cost-miami/";
    const url = `${baseUrl}${path}`;

    const cleanupSeo = injectMetaTags({
      title,
      description: desc,
      url,
      image: "https://impact-window-repair-miami.com/og/og-blog-cost.jpg",
      imageAlt: isEs ? "Guía de precios de reparación de ventanas Miami" : "Window repair cost guide Miami",
      breadcrumbs: [
        { name: isEs ? "Inicio" : "Home", item: baseUrl + "/" },
        { name: "Blog", item: baseUrl + "/blog/" },
        { name: t("blog.cost.h1") as string, item: url }
      ],
      type: "article",
      articleAuthor: "Windows and Doors Repair LLC",
      articlePublisher: baseUrl,
      articleSection: isEs ? "Información de Reparación" : "Window Repair Information",
      articlePublishedTime: "2026-05-18",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: t("blog.cost.h1"),
      inLanguage: isEs ? "es-US" : "en-US",
      author: {
        "@type": "Organization",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
      },
      datePublished: "2026-05-18",
      dateModified: "2026-05-18",
      publisher: {
        "@type": "Organization",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
      },
      description: desc,
    };

    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "blogpost-schema";
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);

    return () => {
      cleanupSeo();
      const el = document.getElementById("blogpost-schema");
      if (el) document.head.removeChild(el);
    };
  }, [currentLang, isEs, t]);

  const getSection = (key: string) => {
    const s = costSections.find((sec) => sec.key === key);
    return s ? (isEs ? s.bodyEs : s.bodyEn) : [];
  };

  const blogPrefix = isEs ? "/es/blog/" : "/blog/";
  const homeLink = isEs ? "/es/" : "/";

  return (
    <main className="bg-white">
      <div className="max-w-5xl mx-auto px-6">

        {/* Article Header */}
        <section className="py-12">
          <nav className="mb-4" aria-label="Breadcrumb">
            <Link to={blogPrefix} className="text-sm text-[#047857] hover:text-[#ea580c]">
              Blog
            </Link>
            <span className="text-sm text-gray-400 mx-2">&gt;</span>
            <span className="text-sm text-gray-500">{t("blog.catPricing")}</span>
          </nav>

          <span className="inline-block bg-[#fbbf24] text-[#064e3b] uppercase tracking-widest text-xs px-3 py-1 rounded">
            {t("blog.catPricing")}
          </span>

          <h1 className="mt-4 text-4xl font-bold text-[#064e3b] leading-tight">
            {t("blog.cost.h1")}
          </h1>

          <p className="mt-4 text-sm text-gray-500">
            {t("blog.publishedOn")} May 2026 &middot; 7 {t("blog.minRead")}
          </p>

          <img
            src="https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/fc4493e6-fb53-42d8-88d9-855e80f7f5c8_Construction-worker-repairing-the-sliding-window.jpg?v=dfaa63348848dd403919e4733d14c615"
            alt={isEs ? "Guía de precios de reparación de ventanas Miami-Dade" : "Window repair pricing guide Miami-Dade"}
            className="mt-8 w-full rounded-xl object-cover"
            width={1000}
            height={500}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/fc4493e6-fb53-42d8-88d9-855e80f7f5c8_Construction-worker-repairing-the-sliding-window.jpg?v=dfaa63348848dd403919e4733d14c615";
            }}
          />
        </section>

        {/* Disclaimer */}
        <div className="bg-[#ea580c]/10 border-l-4 border-[#ea580c] rounded-r-xl p-6 mb-10">
          <p className="text-[#064e3b] font-semibold text-sm leading-relaxed">
            {isEs
              ? "Los rangos a continuación son solo para servicios de reparación y mantenimiento. No somos un contratista con licencia en Florida y no proporcionamos precios de instalación."
              : "Ranges below are for repair and maintenance services only. We are not a licensed Florida contractor and do not provide installation pricing."}
          </p>
        </div>

        {/* Intro */}
        <section className="mb-12">
          {getSection("intro").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* Diagnostic */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.cost.h2Diag")}: $89
          </h2>
          {getSection("diagnostic").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {i === 1 ? (
                <>
                  {p.split("(786) 377-4452")[0]}
                  <a href="tel:+17863774452" className="text-[#047857] font-semibold hover:text-[#ea580c]">(786) 377-4452</a>
                  {p.split("(786) 377-4452")[1]}
                </>
              ) : p}
            </p>
          ))}
        </section>

        {/* Hardware Repair */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.cost.h2Hardware")}: $120 – $280
          </h2>
          {getSection("hardware").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            {[
              { label: isEs ? "Cerradura o manija individual" : "Single lock or handle", price: "$120 – $160" },
              { label: isEs ? "Reemplazo de rodillos (por puerta)" : "Roller replacement (per door)", price: "$180 – $240" },
              { label: isEs ? "Reparación de bisagra o manivela" : "Hinge or crank repair", price: "$140 – $200" },
              { label: isEs ? "Servicio completo puerta corrediza" : "Full sliding door service", price: "$220 – $280" },
            ].map((card, i) => (
              <div key={i} className="bg-[#fefce8] rounded-lg p-4">
                <p className="text-sm text-[#064e3b] font-semibold">{card.label}</p>
                <p className="text-lg font-bold text-[#047857]">{card.price}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Glass Replacement */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.cost.h2Glass")}: $180 – $450
          </h2>
          {getSection("glass").map((p, i) => (
            <p key={i} className={`text-base leading-[1.7] text-[#374151] ${i === 0 ? "mb-4" : "italic mt-4 text-sm"}`}>
              {p}
            </p>
          ))}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            {[
              { label: isEs ? "Pequeño (hasta 24x36)" : "Small (up to 24x36)", price: "$180 – $240" },
              { label: isEs ? "Mediano (hasta 36x48)" : "Medium (up to 36x48)", price: "$260 – $350" },
              { label: isEs ? "Grande (sobre 36x48)" : "Large (over 36x48)", price: "$350 – $450" },
            ].map((card, i) => (
              <div key={i} className="bg-[#fefce8] rounded-lg p-4 text-center">
                <p className="text-sm text-[#064e3b] font-semibold">{card.label}</p>
                <p className="text-lg font-bold text-[#047857]">{card.price}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Weatherstripping */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {isEs ? "Burletes y Sellado" : "Weatherstripping & Sealing"}: $60 – $95 {isEs ? "por abertura" : "per Opening"}
          </h2>
          {getSection("weatherstripping").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* Maintenance */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {isEs ? "Limpieza y Mantenimiento Anual" : "Window Cleaning & Annual Maintenance"}: $129
          </h2>
          {getSection("maintenance").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
          <div className="bg-[#fefce8] rounded-lg p-5 mt-4">
            <p className="text-sm text-[#064e3b] font-semibold mb-2">
              {isEs ? "Qué está incluido:" : "What is included:"}
            </p>
            <ul className="text-sm text-[#374151] space-y-1 list-disc list-inside">
              {costMaintenanceItems.map((item, i) => (
                <li key={i}>{isEs ? item.es : item.en}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Emergency Board-Up */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.cost.h2Emergency")}: {isEs ? "Desde" : "Starts at"} $149
          </h2>
          {getSection("emergency").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* Avoid Overpaying */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-3">
            {t("blog.cost.h2Avoid")}
          </h2>
          {getSection("avoid").map((p, i) => (
            <p key={i} className="text-base leading-[1.7] text-[#374151] mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* Service Links */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-4">
            {isEs ? "Nuestros Servicios de Reparación" : "Our Repair Services"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {costServiceLinks.map((s, i) => (
              <Link
                key={i}
                to={isEs ? `/es${s.to}` : s.to}
                className="bg-white border border-[#047857]/20 rounded-lg p-4 text-[#064e3b] font-semibold text-sm hover:bg-[#fefce8] hover:border-[#047857] transition whitespace-nowrap"
              >
                {isEs ? s.labelEs : s.labelEn} &rarr;
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-6">
            {isEs ? "Preguntas Frecuentes" : "Common Questions"}
          </h2>
          <div className="space-y-4">
            {costFaq.map((faq, i) => (
              <div key={i} className="border border-[#064e3b]/10 rounded-lg p-5">
                <p className="font-semibold text-[#064e3b] mb-2">{isEs ? faq.qEs : faq.qEn}</p>
                <p className="text-sm text-[#374151] leading-relaxed">{isEs ? faq.aEs : faq.aEn}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#ea580c] text-white py-16 my-12 -mx-6 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold">
              {isEs ? "Obtenga una Cotización Telefónica para su Reparación" : "Get a Phone Quote for Your Repair"}
            </h2>
            <p className="mt-4 text-white/90 max-w-xl mx-auto">
              {isEs
                ? "Describa el problema y le daremos un rango de precio realista antes de programar. Sin obligación."
                : "Describe the issue and we will give you a realistic price range before scheduling. No obligation."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+17863774452"
                className="inline-block bg-white text-[#ea580c] font-bold px-8 py-3 rounded-lg hover:bg-[#fefce8] transition whitespace-nowrap"
              >
                {isEs ? "Llame al (786) 377-4452" : "Call (786) 377-4452"}
              </a>
              <Link
                to={homeLink}
                className="inline-block border-2 border-white text-white font-bold px-8 py-3 rounded-lg hover:bg-white hover:text-[#ea580c] transition whitespace-nowrap"
              >
                {isEs ? "Obtener Cotización Telefónica" : "Get Phone Quote"}
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-12">
          <h2 className="text-2xl font-bold text-[#064e3b] mb-6">{t("blog.relatedArticles")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <Link
              to={`${blogPrefix}insurance-discount-impact-windows/`}
              className="bg-white rounded-lg border border-[#064e3b]/10 p-5 hover:shadow-lg hover:border-[#fbbf24]/50 transition block"
            >
              <span className="inline-block bg-[#fbbf24] text-[#064e3b] text-xs uppercase tracking-widest px-3 py-1 rounded">
                {t("blog.catInsurance")}
              </span>
              <h3 className="font-bold text-[#064e3b] mt-3 text-lg leading-snug">
                {t("blog.insurance.h1")}
              </h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                {t("blog.articleInsurance.excerpt")}
              </p>
              <span className="inline-block mt-4 text-[#047857] text-sm font-medium hover:text-[#ea580c]">
                {t("blog.readMore")}
              </span>
            </Link>
            <Link
              to={`${blogPrefix}impact-windows-vs-shutters/`}
              className="bg-white rounded-lg border border-[#064e3b]/10 p-5 hover:shadow-lg hover:border-[#fbbf24]/50 transition block"
            >
              <span className="inline-block bg-[#fbbf24] text-[#064e3b] text-xs uppercase tracking-widest px-3 py-1 rounded">
                {t("blog.catComparison")}
              </span>
              <h3 className="font-bold text-[#064e3b] mt-3 text-lg leading-snug">
                {t("blog.articleShutters.title")}
              </h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                {t("blog.articleShutters.excerpt")}
              </p>
              <span className="inline-block mt-4 text-[#047857] text-sm font-medium hover:text-[#ea580c]">
                {t("blog.readMore")}
              </span>
            </Link>
            <Link
              to={`${blogPrefix}hvhz-code-noa-explained/`}
              className="bg-white rounded-lg border border-[#064e3b]/10 p-5 hover:shadow-lg hover:border-[#fbbf24]/50 transition block"
            >
              <span className="inline-block bg-[#fbbf24] text-[#064e3b] text-xs uppercase tracking-widest px-3 py-1 rounded">
                {t("blog.catCodes")}
              </span>
              <h3 className="font-bold text-[#064e3b] mt-3 text-lg leading-snug">
                {t("blog.articleCodes.title")}
              </h3>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                {t("blog.articleCodes.excerpt")}
              </p>
              <span className="inline-block mt-4 text-[#047857] text-sm font-medium hover:text-[#ea580c]">
                {t("blog.readMore")}
              </span>
            </Link>
          </div>
        </section>

      </div>
    </main>
  );
}